/**
 * Keyboard Shortcuts Handler
 * 
 * Handles keyboard input for building placement and shortcuts.
 * Ported from Flash keyboard event handling in main.mxml
 */

export class KeyboardShortcuts {
    constructor(gameEngine, gridCanvas) {
        this.gameEngine = gameEngine;
        this.gridCanvas = gridCanvas;
        
        // Current keyboard state
        this.ctrlDown = false;
        this.shiftDown = false;
        this.altDown = false;
        
        // Callbacks
        this.onBuildingPlace = null;
        this.onToggleLock = null;
        this.onDelete = null;
        this.onShowHeatMap = null;
        this.onPlaceSuggested = null;
        
        // Setup keyboard handlers
        this.setupEventListeners();
    }
    
    /**
     * Setup keyboard event listeners
     */
    setupEventListeners() {
        document.addEventListener('keydown', (e) => {
            this.handleKeyDown(e);
        });
        
        document.addEventListener('keyup', (e) => {
            this.handleKeyUp(e);
        });
    }
    
    /**
     * Handle key down events
     */
    handleKeyDown(e) {
        // Track modifier keys
        if (e.key === 'Control' || e.key === 'ControlLeft' || e.key === 'ControlRight') {
            this.ctrlDown = true;
        }
        if (e.key === 'Shift' || e.key === 'ShiftLeft' || e.key === 'ShiftRight') {
            this.shiftDown = true;
        }
        if (e.key === 'Alt' || e.key === 'AltLeft' || e.key === 'AltRight') {
            this.altDown = true;
        }
        
        // Don't process shortcuts if typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
            return;
        }
        
        // Prevent default for game shortcuts
        const key = e.key.toLowerCase();
        
        // Handle special keys
        if (e.key === ' ') {
            e.preventDefault();
            if (this.onToggleLock && this.gridCanvas.hoverTile) {
                this.onToggleLock(this.gridCanvas.hoverTile.i, this.gridCanvas.hoverTile.j);
            }
            return;
        }
        
        if (e.key === 'Enter' || e.key === 'Return') {
            e.preventDefault();
            if (this.onPlaceSuggested) {
                this.onPlaceSuggested();
            }
            return;
        }
        
        if (e.key === 'Delete' || e.key === 'Backspace' || key === '`' || key === '0') {
            e.preventDefault();
            if (this.onDelete && this.gridCanvas.hoverTile) {
                this.onDelete(this.gridCanvas.hoverTile.i, this.gridCanvas.hoverTile.j);
            }
            return;
        }
        
        // Handle Shift key for heat map (check for all Shift key variants)
        if (e.key === 'Shift' || e.key === 'ShiftLeft' || e.key === 'ShiftRight') {
            if (this.onShowHeatMap) {
                this.onShowHeatMap(true);
            }
            return;
        }
        
        // Handle Ctrl key for keyboard shortcuts display
        if (e.key === 'Control' || e.key === 'ControlLeft' || e.key === 'ControlRight') {
            // Show keyboard shortcuts panel
            return;
        }
        
        // Handle building placement keys
        if (this.gameEngine && this.gameEngine.keyBindings[key]) {
            e.preventDefault();
            
            if (this.onBuildingPlace && this.gridCanvas.hoverTile) {
                const { i, j } = this.gridCanvas.hoverTile;
                
                // Handle cursor mode for multi-tile buildings
                const cursorMode = this.gridCanvas.cursorMode;
                const [rows, cols] = cursorMode.split('x').map(Number);
                
                // Place building(s) based on cursor size
                // Use the key character directly (e.g., 'f', 's', etc.)
                for (let di = 0; di < rows; di++) {
                    for (let dj = 0; dj < cols; dj++) {
                        const ti = i + di;
                        const tj = j + dj;
                        
                        if (ti < 21 && tj < 21) {
                            this.onBuildingPlace(key, ti, tj);
                        }
                    }
                }
            }
        }
    }
    
    /**
     * Handle key up events
     */
    handleKeyUp(e) {
        // Track modifier keys
        if (e.key === 'Control' || e.key === 'ControlLeft' || e.key === 'ControlRight') {
            this.ctrlDown = false;
        }
        if (e.key === 'Shift' || e.key === 'ShiftLeft' || e.key === 'ShiftRight') {
            this.shiftDown = false;
            if (this.onShowHeatMap) {
                this.onShowHeatMap(false);
            }
        }
        if (e.key === 'Alt' || e.key === 'AltLeft' || e.key === 'AltRight') {
            this.altDown = false;
        }
    }
    
    /**
     * Get key description for display
     */
    getKeyDescription(key) {
        const special = {
            ' ': 'Space',
            'Enter': 'Enter',
            'Delete': 'Del',
            'Backspace': 'Backspace',
            'Control': 'Ctrl',
            'Shift': 'Shift',
            'Alt': 'Alt',
            '`': '`'
        };
        
        return special[key] || key.toUpperCase();
    }
}

